import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import CategoryModal from "../../components/CustomComponents/modal/parentCategoryModel";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import useThemeColor from "../../hooks/useThemeColor";
import { Modifier } from "./initializeModifier.js";

const emptyForm = {
  menuItem_ID: 0,
  menuItem_Name: "",
  menuItem_CostPrice: "",
  menuItem_TaxPercentage: "",
  menuItem_SalePrice: "",
  menuItem_DeliveryPrice: "",
  menuItem_TakeAwayPrice: "",
  reportCat_ID: "",
  reportCat_Name: "",
  is_OutOfStock: false,
};

const currencySymbols = {
  GBP: "£",
  USD: "$",
  EUR: "€",
};

const safeParseFloat = (value, defaultVal = 0) => {
  if (value === null || value === undefined || value === "") return defaultVal;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? defaultVal : parsed;
};

/** SQL Server rejects DateTime.MinValue (0001-01-01); treat as no expiry. */
const sanitizeExpiryDate = (raw) => {
  if (!raw || raw === "0001-01-01T00:00:00" || String(raw).startsWith("0001-01-01")) {
    return null;
  }
  return raw;
};

const getCategoryId = (category) =>
  category?.rootCat_ID ??
  category?.RootCat_ID ??
  category?.rC_ID ??
  category?.RC_ID ??
  category?.category_ID ??
  category?.id ??
  "";

const getCategoryName = (category) =>
  category?.rootCat_Name ||
  category?.RootCat_Name ||
  category?.rC_Name ||
  category?.RC_Name ||
  category?.reportingCategoryName ||
  category?.ReportingCategoryName ||
  "";

const findCategoryById = (cats, categoryId) => {
  if (!categoryId || !Array.isArray(cats) || cats.length === 0) return null;
  const target = Number(categoryId);
  for (const c of cats) {
    const cId = getCategoryId(c);
    if (cId === target || String(cId) === String(categoryId)) return c;
    const children = c?.subCategoriesList || c?.SubCategoriesList;
    if (Array.isArray(children) && children.length > 0) {
      const found = findCategoryById(children, categoryId);
      if (found) return found;
    }
  }
  return null;
};

const pickReportCatId = (source) => {
  if (!source) return "";
  const raw =
    source.reportCat_ID ??
    source.ReportCat_ID ??
    source.rootCat_ID ??
    source.RootCat_ID ??
    "";
  if (raw === null || raw === undefined || raw === "" || Number(raw) === 0) {
    return "";
  }
  return raw;
};

const pickReportCatName = (source) => {
  if (!source) return "";
  return (
    source.reportCat_Name ||
    source.ReportCat_Name ||
    source.rootCat_Name ||
    source.RootCat_Name ||
    ""
  );
};

const ModifierFormModal = ({
  isOpen,
  onClose,
  onSaved,
  editingModifier = null,
  compCurrency = "GBP",
}) => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { settingsData } = useSelector((state) => state.auth);
  const themeColor = useThemeColor();

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const isEdit = Boolean(editingModifier?.menuItem_ID);
  const currencySymbol = currencySymbols[compCurrency] || compCurrency;

  const fetchCategories = useCallback(
    async (options = {}) => {
      if (!CompID) return [];
      try {
        if (options.showLoader !== false && options.showGlobalLoading !== false) {
          setCategoriesLoading(true);
        }
        const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
          params: {
            CompID,
            SortBy: "RootCat_Name",
            SortOrder: "ASC",
            Search: "",
          },
        });
        const categoryData = response.data.data || response.data;
        const list = Array.isArray(categoryData) ? categoryData : [];
        setCategories(list);
        return list;
      } catch {
        setCategories([]);
        showErrorToast("Failed to fetch categories");
        return [];
      } finally {
        setCategoriesLoading(false);
      }
    },
    [CompID]
  );

  useEffect(() => {
    if (!isOpen) return;

    const defaultTax =
      settingsData?.isTaxEnabled && settingsData?.s_VAT
        ? String(settingsData.s_VAT)
        : "";

    if (editingModifier?.menuItem_ID) {
      setForm({
        menuItem_ID: editingModifier.menuItem_ID,
        menuItem_Name: editingModifier.menuItem_Name || "",
        menuItem_CostPrice:
          editingModifier.menuItem_CostPrice === 0 ||
          editingModifier.menuItem_CostPrice
            ? String(editingModifier.menuItem_CostPrice)
            : "",
        menuItem_TaxPercentage:
          editingModifier.menuItem_TaxPercentage === 0 ||
          editingModifier.menuItem_TaxPercentage
            ? String(editingModifier.menuItem_TaxPercentage)
            : defaultTax,
        menuItem_SalePrice:
          editingModifier.menuItem_SalePrice === 0 ||
          editingModifier.menuItem_SalePrice
            ? String(editingModifier.menuItem_SalePrice)
            : "",
        menuItem_DeliveryPrice:
          editingModifier.menuItem_DeliveryPrice === 0 ||
          editingModifier.menuItem_DeliveryPrice
            ? String(editingModifier.menuItem_DeliveryPrice)
            : "",
        menuItem_TakeAwayPrice:
          editingModifier.menuItem_TakeAwayPrice === 0 ||
          editingModifier.menuItem_TakeAwayPrice
            ? String(editingModifier.menuItem_TakeAwayPrice)
            : "",
        reportCat_ID: pickReportCatId(editingModifier),
        reportCat_Name: pickReportCatName(editingModifier),
        is_OutOfStock: Boolean(editingModifier.is_OutOfStock),
      });
    } else {
      setForm({
        ...emptyForm,
        menuItem_TaxPercentage: defaultTax,
      });
    }

    fetchCategories({ showLoader: false, showGlobalLoading: false });
  }, [isOpen, editingModifier, settingsData, fetchCategories]);

  // Backfill category name when API returned ReportCat_ID without RootCat_Name
  useEffect(() => {
    if (!isOpen || !form.reportCat_ID || form.reportCat_Name) return;
    if (!categories.length) return;
    const found = findCategoryById(categories, form.reportCat_ID);
    const name = getCategoryName(found);
    if (!name) return;
    setForm((prev) =>
      prev.reportCat_ID && !prev.reportCat_Name
        ? { ...prev, reportCat_Name: name }
        : prev
    );
  }, [isOpen, form.reportCat_ID, form.reportCat_Name, categories]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const applyCategory = (categoryData) => {
    if (!categoryData) return;
    const id = getCategoryId(categoryData);
    const name = getCategoryName(categoryData);
    if (!id) return;
    setForm((prev) => ({
      ...prev,
      reportCat_ID: id,
      reportCat_Name: name || "",
    }));
  };

  const buildPayloadFromForm = (existing = null) => {
    const sale = safeParseFloat(form.menuItem_SalePrice);
    const delivery =
      form.menuItem_DeliveryPrice === "" ||
      form.menuItem_DeliveryPrice === null ||
      form.menuItem_DeliveryPrice === undefined
        ? sale
        : safeParseFloat(form.menuItem_DeliveryPrice);
    const takeAway =
      form.menuItem_TakeAwayPrice === "" ||
      form.menuItem_TakeAwayPrice === null ||
      form.menuItem_TakeAwayPrice === undefined
        ? sale
        : safeParseFloat(form.menuItem_TakeAwayPrice);

    const shortFields = {
      menuItem_ID: form.menuItem_ID || 0,
      compID: CompID,
      menuItem_Name: form.menuItem_Name.trim(),
      menuItem_KitchenDisplayName:
        form.menuItem_Name.trim() ||
        existing?.menuItem_KitchenDisplayName ||
        "",
      menuItem_CostPrice: safeParseFloat(form.menuItem_CostPrice),
      menuItem_TaxPercentage: safeParseFloat(form.menuItem_TaxPercentage),
      menuItem_SalePrice: sale,
      menuItem_DeliveryPrice: delivery,
      menuItem_TakeAwayPrice: takeAway,
      reportCat_ID: Number(form.reportCat_ID) || 0,
      is_OutOfStock: Boolean(form.is_OutOfStock),
      menuItem_IsModifier: true,
      menuItem_Type: "modifier",
      menuItem_Publish: existing?.menuItem_Publish ?? true,
    };

    if (existing) {
      return {
        ...existing,
        ...shortFields,
        menuItem_ExpiryDate: sanitizeExpiryDate(existing.menuItem_ExpiryDate),
        menuItem_DateModified: new Date().toISOString(),
      };
    }

    return {
      ...Modifier,
      menuItem_ID: 0,
      localizeID: "en",
      menuItem_DataCreated: new Date().toISOString(),
      menuItem_DateModified: new Date().toISOString(),
      menuItem_ExpiryDate: null,
      ...shortFields,
    };
  };

  const handleSubmit = async () => {
    if (!form.menuItem_Name?.trim()) {
      showErrorToast("Modifier name is required");
      return;
    }
    if (!form.reportCat_ID) {
      showErrorToast("Please select a reporting category");
      return;
    }

    try {
      setSaving(true);

      if (isEdit) {
        const existingResponse = await axiosInstance.get(APIs.GET_ITEM_BY_ID, {
          params: {
            CompID,
            MenuItem_ID: form.menuItem_ID,
          },
        });

        if (!existingResponse.data?.status || !existingResponse.data?.data) {
          showErrorToast(
            existingResponse.data?.message || "Failed to load modifier"
          );
          return;
        }

        const payload = buildPayloadFromForm(existingResponse.data.data);
        const response = await axiosInstance.put(APIs.PUT_UPDATE_ITEM, payload);

        if (response.data?.status) {
          showSuccessToast("Modifier updated successfully");
          onSaved?.();
          onClose?.();
        } else {
          showErrorToast(response.data?.message || "Failed to update modifier");
        }
      } else {
        const payload = buildPayloadFromForm();
        const response = await axiosInstance.post(
          APIs.POST_CREATE_MENU_ITEM,
          payload
        );

        if (response.data?.status) {
          showSuccessToast("Modifier added successfully");
          onSaved?.();
          onClose?.();
        } else {
          showErrorToast(response.data?.message || "Failed to add modifier");
        }
      }
    } catch (error) {
      showErrorToast(
        error.response?.data?.message ||
          (isEdit
            ? "An error occurred while updating the modifier"
            : "An error occurred while adding the modifier")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? "Update Modifier" : "Add Modifier"}
        subtitle="Short details used in modifier groups"
        fontSize="18px"
        fontWeight={400}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText={isEdit ? "Update" : "Save"}
        submitButtonDisabled={saving || !form.menuItem_Name?.trim()}
        onSubmit={handleSubmit}
        modalSize="modal-lg"
        minHeight="auto"
        maxHeight="90vh"
        zIndexBase={9999}
      >
        <GridSectionLoader loading={saving} minHeight="220px">
          <div className="row g-3">
            <div className="col-md-6">
              <FormInput
                label="Modifier"
                placeHolder="Enter modifier name"
                type="text"
                value={form.menuItem_Name}
                onchange={(e) => handleChange("menuItem_Name", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <FormInput
                label={`Cost Price (${currencySymbol})`}
                placeHolder="0.00"
                type="number"
                step="any"
                min="0"
                value={form.menuItem_CostPrice}
                onchange={(e) =>
                  handleChange("menuItem_CostPrice", e.target.value)
                }
              />
            </div>
            <div className="col-md-6">
              <FormInput
                label="Tax (%)"
                placeHolder="0.00"
                type="number"
                step="any"
                min="0"
                max="100"
                value={form.menuItem_TaxPercentage}
                onchange={(e) =>
                  handleChange("menuItem_TaxPercentage", e.target.value)
                }
              />
            </div>
            <div className="col-md-6">
              <FormInput
                label={`Price (${currencySymbol})`}
                placeHolder="0.00"
                type="number"
                step="any"
                min="0"
                value={form.menuItem_SalePrice}
                onchange={(e) =>
                  handleChange("menuItem_SalePrice", e.target.value)
                }
              />
            </div>
            <div className="col-md-6">
              <FormInput
                label={`Delivery Price (${currencySymbol})`}
                placeHolder="0.00"
                type="number"
                step="any"
                min="0"
                value={form.menuItem_DeliveryPrice}
                onchange={(e) =>
                  handleChange("menuItem_DeliveryPrice", e.target.value)
                }
              />
            </div>
            <div className="col-md-6">
              <FormInput
                label={`Take Away Price (${currencySymbol})`}
                placeHolder="0.00"
                type="number"
                step="any"
                min="0"
                value={form.menuItem_TakeAwayPrice}
                onchange={(e) =>
                  handleChange("menuItem_TakeAwayPrice", e.target.value)
                }
              />
            </div>
            <div className="col-md-6">
              <FormInput
                label="Reporting Category"
                placeHolder="Select Reporting Category"
                type="text"
                value={form.reportCat_Name || ""}
                onchange={() => {}}
                onclick={() => setShowCategoryModal(true)}
                readOnly
                icon="fa-caret-down"
                iconOnClick={() => setShowCategoryModal(true)}
                secIcon={form.reportCat_ID ? "fa-times-circle" : ""}
                secIconOnClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    reportCat_ID: "",
                    reportCat_Name: "",
                  }))
                }
              />
            </div>
            <div className="col-md-6 d-flex align-items-end pb-2">
              <ToggleSwitch
                isChecked={!form.is_OutOfStock}
                onToggle={() =>
                  handleChange("is_OutOfStock", !form.is_OutOfStock)
                }
                label="In stock"
                disabled={false}
                themeColor={themeColor}
              />
            </div>
          </div>
        </GridSectionLoader>
      </CustomModal>

      <CategoryModal
        showParentCategoryModal={showCategoryModal}
        setShowParentCategoryModal={setShowCategoryModal}
        setSelectedParentCategory={applyCategory}
        onSubmit={(categoryData) => {
          applyCategory(categoryData);
          setShowCategoryModal(false);
        }}
        compID={CompID}
        selectedParentCategory={
          form.reportCat_ID
            ? {
                rootCat_ID: Number(form.reportCat_ID) || form.reportCat_ID,
                rootCat_Name: form.reportCat_Name || "",
              }
            : null
        }
        categories={categories}
        categoriesLoading={categoriesLoading}
        showButton={true}
        isMultiSelect={false}
        modalTitle="Select Reporting Category"
        modalSubTitle="Select a reporting category for the modifier"
        fetchCategories={fetchCategories}
      />
    </>
  );
};

export default ModifierFormModal;
